import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { TRADES, COUNTIES, DURATIONS } from "@/constants/data";
import CountiesPicker from "@/components/CountiesPicker";

export default function PostAvailability() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    headline: "",
    trade: TRADES[0],
    county: COUNTIES[0],
    counties: [],
    years_experience: 5,
    day_rate: "",
    available_from: "",
    availability: DURATIONS[0],
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        ...f,
        years_experience: parseInt(f.years_experience, 10) || 0,
        day_rate: f.day_rate ? parseFloat(f.day_rate) : null,
        available_from: f.available_from || null,
      };
      const { data } = await api.post("/workers", payload);
      toast.success("Availability posted");
      navigate(`/workers/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Post Availability</div>
      <h1 className="display text-4xl md:text-5xl mt-2">Get Called for Work</h1>
      <p className="text-neutral-400 mt-2">Tell contractors what you do, when you&apos;re free, and your day rate.</p>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="post-availability-form">
        <Field label="Headline" required>
          <input data-testid="avail-headline-input" required value={f.headline} onChange={set("headline")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="e.g. Experienced welder available weekends" />
        </Field>
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Trade" required>
            <select data-testid="avail-trade-select" value={f.trade} onChange={set("trade")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
              {TRADES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Availability" required>
            <select data-testid="avail-window-select" value={f.availability} onChange={set("availability")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
              {DURATIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="County" required>
            <select data-testid="avail-county-select" value={f.county} onChange={set("county")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
              {COUNTIES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Years of Experience" required>
            <input data-testid="avail-years-input" type="number" min="0" max="60" required value={f.years_experience} onChange={set("years_experience")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" />
          </Field>
          <Field label="Available From">
            <input data-testid="avail-from-input" type="date" value={f.available_from} onChange={set("available_from")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3 text-neutral-200" />
          </Field>
          <Field label="Day Rate (€)">
            <input data-testid="avail-rate-input" type="number" min="0" step="10" value={f.day_rate} onChange={set("day_rate")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="220" />
          </Field>
        </div>
        <Field label="Also willing to work in (optional)">
          <div className="text-[11px] text-neutral-500 -mt-1 mb-2">
            Tick every county you'd travel to. Contractors searching those counties will see your profile too.
          </div>
          <CountiesPicker
            primary={f.county}
            value={f.counties}
            onChange={(next) => setF((s) => ({ ...s, counties: next }))}
          />
          {f.counties.length > 0 && (
            <div className="mt-2 text-[11px] uppercase tracking-widest text-amber-500">
              {f.counties.length} extra {f.counties.length === 1 ? "county" : "counties"} selected · more jobs will find you
            </div>
          )}
        </Field>
        <Field label="About / Skills" required>
          <textarea data-testid="avail-description-input" required rows={6} value={f.description} onChange={set("description")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="What you can do, safety tickets/certs (Safe Pass, CSCS), own transport, tools, references…" />
        </Field>

        {error && <div data-testid="post-avail-error" className="border border-red-800 bg-red-950/40 text-red-300 px-4 py-3 text-sm">{error}</div>}

        <button data-testid="post-avail-submit" disabled={loading} className="w-full md:w-auto px-8 py-3 bg-amber-500 text-neutral-950 font-bold uppercase tracking-widest hover:bg-amber-400 transition-colors disabled:opacity-60">
          {loading ? "Posting…" : "Post Availability"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">
        {label}{required && <span className="text-amber-500"> *</span>}
      </label>
      {children}
    </div>
  );
}
